import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import FileUpload from '../components/FileUpload';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import FeatureGate from '../components/FeatureGate';
import * as pdfjsLib from 'pdfjs-dist';
import hljs from 'highlight.js';
import BackButton from '../components/BackButton';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const DOWNLOAD_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartdata-download-file';
const DELETE_URL   = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartdata-delete-file';
const SIGN_DERIVED_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartdata-sign-derived';

const CATEGORY_ICONS = {
  image: '🖼️', document: '📄', spreadsheet: '📊', presentation: '📽️',
  audio: '🎵', video: '🎬', archive: '🗜️', code: '💻', config: '⚙️',
  design: '🎨', financial: '💶', data: '🗄️', executable: '🧩',
  security: '🔐', other: '📁',
};

const CATEGORY_LABELS = {
  image: 'Images', document: 'Documents', spreadsheet: 'Spreadsheets',
  presentation: 'Presentations', audio: 'Audio', video: 'Video',
  archive: 'Archives', code: 'Code', config: 'Config / IaC', design: 'Design',
  financial: 'Financial', data: 'Data', executable: 'Executables',
  security: 'Security / Keys', other: 'Other',
};

const L2_PACK_ICONS = {
  finance: '💶', legal: '⚖️', medical: '🩺',
  contacts: '👤', general: '🏢',
};

const L2_PACKS = {
  finance:  ['invoice','receipt','bank statement','profit and loss','balance sheet','tax document','payroll','purchase order','quote','financial statement'],
  legal:    ['contract','NDA','agreement','terms and conditions','court filing','deed','legal letter','power of attorney','compliance document'],
  medical:  ['prescription','lab result','medical report','referral','scan','consent form','insurance claim','discharge summary'],
  contacts: ['business card','contact list','email signature','directory','vCard'],
  general:  ['letter','report','CV','resume','certificate','ID document','screenshot','diagram','logo','presentation slide','form'],
};

function getL2Pack(label) {
  for (const [pack, labels] of Object.entries(L2_PACKS)) {
    if (labels.includes(label)) return pack;
  }
  return null;
}

function getFilePacks(file) {
  if (!file.content_labels?.length) return [];
  const packs = new Set(file.content_labels.map(getL2Pack).filter(Boolean));
  return [...packs];
}

function SmartData({ gido, quota, onBack }) {

  const [files, setFiles]             = useState([]);
  const [signedUrls, setSignedUrls]   = useState({});  // {file_id: {thumbnail, preview}}
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterCategory, setFilter]   = useState('all');
  const [filterPack, setFilterPack]   = useState('all');
  const [sortBy, setSortBy]           = useState('date');
  const [view, setView]               = useState('grid');
  const [selectedFile, setSelected]   = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [lightbox, setLightbox]       = useState(null);

  const gidoId      = gido?.gido_id || '';
  const companyName = gido?.company_name || gido?.display_name || 'SmartClient';

  useEffect(() => {
    if (!gidoId) return;
    loadFiles();
  }, [gidoId]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const filesRef = collection(db, 'smartdata_quotas', gidoId, 'files');
      const q        = query(filesRef, orderBy('uploaded_at', 'desc'));
      const snapshot = await getDocs(q);

      const fileList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploaded_at: doc.data().uploaded_at?.toDate
          ? doc.data().uploaded_at.toDate()
          : new Date(doc.data().uploaded_at),
      }));

      setFiles(fileList);
      fetchDerivedUrls(fileList);  // Phase 4.2 pointer-fix: batch-sign thumbnail/preview URLs
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFiles = files.filter(file => {
    if (file.status === 'deleted') return false;
    const matchesSearch = search === '' ||
      file.filename?.toLowerCase().includes(search.toLowerCase()) ||
      file.ai_labels?.some(label => label.toLowerCase().includes(search.toLowerCase())) ||
      file.ocr_text?.toLowerCase().includes(search.toLowerCase()) ||
      file.auto_path?.toLowerCase().includes(search.toLowerCase());

      const matchesCategory = filterCategory === 'all' || file.category === filterCategory;

      const matchesPack = filterPack === 'all' ||
        (file.content_labels?.some(label => getL2Pack(label) === filterPack));
  
      return matchesSearch && matchesCategory && matchesPack;
  });

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    if (sortBy === 'date')      return b.uploaded_at - a.uploaded_at;
    if (sortBy === 'date_asc')  return a.uploaded_at - b.uploaded_at;
    if (sortBy === 'name')      return a.filename?.localeCompare(b.filename);
    if (sortBy === 'name_desc') return b.filename?.localeCompare(a.filename);
    if (sortBy === 'size')      return b.size_bytes - a.size_bytes;
    if (sortBy === 'size_asc')  return a.size_bytes - b.size_bytes;
    if (sortBy === 'category')  return a.category?.localeCompare(b.category);
    return 0;
  });

  const categories = ['all', ...new Set(files.map(f => f.category).filter(Boolean))];

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024)        return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Phase 4.2 pointer-fix: batch-mint signed URLs for _derived/ thumbnail+preview assets.
  // ONE call for all visible files (never per-file). Stores {file_id: {thumbnail, preview}}.
  const fetchDerivedUrls = async (fileList) => {
    const items = [];
    for (const f of fileList) {
      if (f.thumbnail_path) items.push({ file_id: f.file_id, kind: 'thumbnail' });
      if (f.preview_path)   items.push({ file_id: f.file_id, kind: 'preview' });
    }
    if (items.length === 0) return;
    try {
      await auth.authStateReady();            // wait for Firebase auth to settle (fixes first-paint race)
      if (!auth.currentUser) return;          // not signed in yet — skip; a later load will retry
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(SIGN_DERIVED_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken, gido_id: gidoId, items }),
      });
      const data = await res.json();
      const map = {};
      for (const u of (data.urls || [])) {
        if (!map[u.file_id]) map[u.file_id] = {};
        map[u.file_id][u.kind] = u.url;
      }
      console.log('[SIGN-DERIVED] got', Object.keys(map).length, 'file url-sets');  // Stage-1 proof; remove later
      setSignedUrls(prev => ({ ...prev, ...map }));
    } catch (e) {
      console.error('[SIGN-DERIVED] fetch failed', e);
    }
  };

  const handleDownload = async (file) => {
    setDownloading(true);
    try {
      const idToken  = await auth.currentUser.getIdToken();
      const response = await fetch(DOWNLOAD_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          id_token: idToken,
          file_id:  file.file_id,
          gido_id:  gidoId,
        }),
      });
      const data = await response.json();
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const getSignedUrl = async (file) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(DOWNLOAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken, file_id: file.file_id, gido_id: gidoId }),
      });
      const data = await res.json();
      return data.download_url || null;
    } catch (e) { return null; }
  };

  const handleDelete = async (file) => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    setSelected(null);
    setDeleteConfirm(false);
    setFiles(prev => prev.filter(f => f.file_id !== file.file_id));
    try {
      const idToken  = await auth.currentUser.getIdToken();
      const response = await fetch(DELETE_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          id_token: idToken,
          file_id:  file.file_id,
          gido_id:  gidoId,
        }),
      });
      const data = await response.json();
      if (data.status !== 'deleted') throw new Error('not confirmed');
    } catch (err) {
      console.error('Delete failed, restoring file:', err);
      setFiles(prev => [file, ...prev]);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-IE', {
      day:   '2-digit',
      month: 'short',
      year:  'numeric',
    });
  };

  const storageUsed  = files
    .filter(f => f.status !== 'deleted')
    .reduce((sum, f) => sum + (f.size_bytes || 0), 0) / (1024 ** 3);
  const storageTotal = quota?.storage_gb || 25;
  const storagePct   = Math.min((storageUsed / storageTotal) * 100, 100);
  const aiUsed       = quota?.ai_labels_used_month || 0;
  const aiTotal      = quota?.ai_labels_per_month || 500;

  return (
    <div className="smartdata-page">

      <header className="portal-header">
        <div className="header-left">
          <BackButton onClick={onBack} label="Portal" />
          <span className="header-title">SmartData</span>
          <span className="layer-badge">Layer I</span>
        </div>
        <div className="header-right">
          <span className="gido-badge">{gidoId}</span>
          <button className="refresh-button" onClick={loadFiles}>↻ Refresh</button>
        </div>
      </header>

      <div className="smartdata-page-content">

        <div className="smartdata-stats">
          <div className="stat-card">
            <div className="stat-value">{files.filter(f => f.status !== 'deleted').length}</div>
            <div className="stat-label">Total Files</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{storageUsed.toFixed(2)} GB</div>
            <div className="stat-label">of {storageTotal} GB used</div>
            <div className="mini-progress">
              <div className="mini-progress-fill" style={{ width: `${storagePct}%` }} />
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{aiUsed}</div>
            <div className="stat-label">of {aiTotal} AI labels used</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{quota?.tier || 'trial'}</div>
            <div className="stat-label">Current tier</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">🔒</div>
            <div className="stat-label">Your Private Cloud</div>
          </div>
        </div>

        <FileUpload
          gido={gido}
          quota={quota}
          onUploadComplete={() => { loadFiles(); }}
        />

        <div className="smartdata-actions-bar">
          <div className="files-counter">
            {filterCategory === 'all'
              ? `${sortedFiles.length} files`
              : `${sortedFiles.length} of ${files.filter(f => f.status !== 'deleted').length} files · ${CATEGORY_LABELS[filterCategory] || filterCategory}`
            }
          </div>
          <div className="view-controls">
            <button
              className={`view-btn ${view === 'grid' ? 'active' : ''}`}
              onClick={() => setView('grid')}
            >⊞ Grid</button>
            <button
              className={`view-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
            >☰ List</button>
            <FeatureGate
              enabled={quota?.features?.bulk_operations === true}
              plan="Plus and above"
              blurb="Bulk operations — select and delete or organise many files at once. Available from the Plus plan."
            >
              <button className="view-btn">☑ Select</button>
            </FeatureGate>
          </div>
        </div>

        <div className="smartdata-controls">
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search files, AI labels, content..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch('')}>✕</button>
            )}
          </div>

          <div className="filter-controls">
            <select
              value={filterPack}
              onChange={(e) => setFilterPack(e.target.value)}
              className="filter-select filter-content"
            >
              <option value="all">✨ Content</option>
              <option value="finance">💶 Finance</option>
              <option value="legal">⚖️ Legal</option>
              <option value="medical">🩺 Medical</option>
              <option value="contacts">👤 Contacts</option>
              <option value="general">🏢 General</option>
            </select>

            <select
              value={filterCategory}
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : `${CATEGORY_ICONS[cat] || '📁'} ${CATEGORY_LABELS[cat] || cat}`}
                </option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="date">Sort: Latest first</option>
              <option value="date_asc">Sort: Oldest first</option>
              <option value="name">Sort: Name A-Z</option>
              <option value="name_desc">Sort: Name Z-A</option>
              <option value="size">Sort: Largest first</option>
              <option value="size_asc">Sort: Smallest first</option>
              <option value="category">Sort: Category A-Z</option>
            </select>
          </div>
        </div>

        {search && (
          <div className="results-count">
            {sortedFiles.length} result{sortedFiles.length !== 1 ? 's' : ''} for "{search}"
          </div>
        )}

        {selectedFile && (
          <FileDetailPanel
            file={selectedFile}
            onClose={() => { setSelected(null); setDeleteConfirm(false); }}
            files={sortedFiles}
            onSelect={setSelected}
            onDownload={handleDownload}
            onDelete={handleDelete}
            downloading={downloading}
            deleting={deleting}
            deleteConfirm={deleteConfirm}
            formatSize={formatSize}
            formatDate={formatDate}
            onExpand={setLightbox}
            getSignedUrl={getSignedUrl}
            signedUrls={signedUrls}
            quota={quota}
          />
        )}

        {lightbox && (
          <Lightbox
            file={lightbox}
            files={sortedFiles}
            onSelect={setLightbox}
            onClose={() => { setSelected(lightbox); setLightbox(null); }}
            getSignedUrl={getSignedUrl}
            signedUrls={signedUrls}
          />
        )}

        {loading ? (
          <div className="files-loading">
            <div className="loading-spinner"></div>
            <span>Loading your files...</span>
          </div>
        ) : sortedFiles.length === 0 ? (
          <div className="files-empty">
            {files.length === 0 ? (
              <>
                <div className="empty-icon">📂</div>
                <h3>No files yet</h3>
                <p>Upload your first file to get started.</p>
                <p className="empty-hint">Direct upload coming in V1.0.1</p>
              </>
            ) : (
              <>
                <div className="empty-icon">🔍</div>
                <h3>No results found</h3>
                <p>Try a different search term or category.</p>
              </>
            )}
          </div>
        ) : view === 'grid' ? (
          <div className="files-grid">
            {sortedFiles.map(file => (
              <FileCard key={file.id} file={file} signedUrls={signedUrls} formatSize={formatSize} formatDate={formatDate} onClick={() => setSelected(file)} />
            ))}
          </div>
        ) : (
          <div className="files-list">
            {sortedFiles.map(file => (
              <FileRow key={file.id} file={file} signedUrls={signedUrls} formatSize={formatSize} formatDate={formatDate} onClick={() => setSelected(file)} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// FILE CARD (Grid view)
// ─────────────────────────────────────────────

function FileCard({ file, signedUrls, formatSize, formatDate, onClick }) {
  const showTextPreview = file.ocr_text && (
    file.category === 'document' ||
    file.category === 'spreadsheet' ||
    file.extension === 'odt' ||
    file.extension === 'docx' ||
    file.extension === 'doc'
  );

  return (
    <div className="file-card" onClick={onClick}>
      <div className="file-card-icon">
        {(signedUrls?.[file.file_id]?.thumbnail || file.thumbnail_b64)
          ? <img src={signedUrls?.[file.file_id]?.thumbnail || file.thumbnail_b64} alt="" className="file-card-thumb" onError={(e)=>{e.target.style.display='none';}} />
          : (CATEGORY_ICONS[file.category] || '📁')}
      </div>
      <div className="file-card-name" title={file.filename}>
        {file.filename}
      </div>
      <div className="file-card-meta">
      <span className="file-category">{file.category}{file.extension ? ` · ${file.extension.toUpperCase()}` : ''}</span>
        <span className="file-size">{formatSize(file.size_bytes)}</span>
      </div>
      <div className="file-card-date">{formatDate(file.uploaded_at)}</div>
      {showTextPreview && (
        <div className="file-card-preview">
          {(file.ocr_text || '').split('\n').filter(l => l.trim()).slice(0, 3).join(' ').slice(0, 140)}…
        </div>
      )}
      {getFilePacks(file).length > 0 && (
        <div className="file-labels">
          {getFilePacks(file).map(pack => (
            <span key={pack} className={`file-label l2-${pack}`}>
              {L2_PACK_ICONS[pack]} {pack}
            </span>
          ))}
        </div>
      )}
      {file.content_labels?.length > 0 && (
        <div className="file-labels">
          {file.content_labels.slice(0, 4).map((label, i) => (
            <span key={i} className="file-label file-label-content">{label}</span>
          ))}
        </div>
      )}
      {file.ai_labels?.length > 0 && (
        <div className="file-labels">
          {file.ai_labels.slice(0, 4).map((label, i) => (
            <span key={i} className="file-label">{label}</span>
          ))}
        </div>
      )}
      {file.auto_path && (
        <div className="file-path">{file.auto_path}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// FILE ROW (List view)
// ─────────────────────────────────────────────

function FileRow({ file, signedUrls, formatSize, formatDate, onClick }) {
  return (
    <div className="file-row" onClick={onClick}>
      <div className="file-row-icon">
        {(signedUrls?.[file.file_id]?.thumbnail || file.thumbnail_b64)
          ? <img src={signedUrls?.[file.file_id]?.thumbnail || file.thumbnail_b64} alt="" className="file-row-thumb" onError={(e)=>{e.target.style.display='none';}} />
          : (CATEGORY_ICONS[file.category] || '📁')}
      </div>
      <div className="file-row-name">{file.filename}</div>
      <div className="file-row-category">{file.category}{file.extension ? ` · ${file.extension.toUpperCase()}` : ''}</div>
      <div className="file-row-path">{file.auto_path}</div>
      <div className="file-row-size">{formatSize(file.size_bytes)}</div>
      <div className="file-row-date">{formatDate(file.uploaded_at)}</div>
      <div className="file-row-labels">
        {file.ai_labels?.slice(0, 3).map((label, i) => (
          <span key={i} className="file-label">{label}</span>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// FILE DETAIL PANEL
// ─────────────────────────────────────────────

function FileDetailPanel({ file, onClose, onDownload, onDelete, downloading, deleting, deleteConfirm, formatSize, formatDate, files = [], onSelect, onExpand, getSignedUrl, signedUrls, quota }) {

  const [pdfUrl, setPdfUrl]     = React.useState(null);
  const [pdfLoading, setPdfLoading] = React.useState(false);

  const isDeleted = file.status === 'deleted';
  const ext = (file.extension || '').toLowerCase();

  const INLINE_PREVIEW_EXTS = ['pdf', 'docx', 'doc', 'odt', 'xlsx', 'xls'];
  const showInlinePreview = INLINE_PREVIEW_EXTS.includes(ext) || file.ocr_text;

  const idx     = files.findIndex(f => f.file_id === file.file_id);
  const hasPrev = idx > 0;
  const hasNext = idx >= 0 && idx < files.length - 1;
  const goPrev  = () => { if (hasPrev && onSelect) onSelect(files[idx - 1]); };
  const goNext  = () => { if (hasNext && onSelect) onSelect(files[idx + 1]); };

  // Load signed URL for PDF inline preview in detail panel
  React.useEffect(() => {
    setPdfUrl(null);
    if (ext === 'pdf' && getSignedUrl) {
      setPdfLoading(true);
      getSignedUrl(file).then(u => { setPdfUrl(u); setPdfLoading(false); });
    }
  }, [file.file_id]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="file-detail-overlay" onClick={onClose}>
      <div className="file-detail-panel" onClick={(e) => e.stopPropagation()}>

        <div className="file-detail-header">
          <div className="file-detail-icon">{CATEGORY_ICONS[file.category] || '📁'}</div>
          <div className="file-detail-title">
            <div className="file-detail-name">{file.filename}</div>
            <div className="file-detail-meta">
              {file.category} · {(file.extension || '').toUpperCase()} · {formatSize(file.size_bytes)} · {file.tier}
            </div>
          </div>
          <button className="file-detail-close" onClick={onClose}>✕</button>
        </div>

        {files.length > 1 && (
          <div className="file-detail-nav">
            <button className="nav-arrow" onClick={goPrev} disabled={!hasPrev}>‹ Prev</button>
            <span className="nav-pos">{idx + 1} of {files.length}</span>
            <button className="nav-arrow" onClick={goNext} disabled={!hasNext}>Next ›</button>
          </div>
        )}

        <div className="file-detail-body">

          {/* Image thumbnail — click to expand */}
          {(signedUrls?.[file.file_id]?.preview || signedUrls?.[file.file_id]?.thumbnail || file.preview_b64 || file.thumbnail_b64) && (
            <div className="detail-preview-image" onClick={() => onExpand && onExpand(file)} title="Click to expand">
              <img src={signedUrls?.[file.file_id]?.preview || signedUrls?.[file.file_id]?.thumbnail || file.preview_b64 || file.thumbnail_b64} alt={file.filename} onError={(e)=>{e.target.style.display='none';}} />
            </div>
          )}

          {/* PDF inline preview in detail panel */}
          {ext === 'pdf' && (
            <div className="detail-section">
              <div className="detail-section-title">📄 PDF Preview</div>
              {pdfLoading && <div className="detail-pdf-loading">Loading preview…</div>}
              {pdfUrl && !pdfLoading && (
                <DetailPdfViewer url={pdfUrl} />
              )}
              <button className="detail-expand-btn" onClick={() => onExpand && onExpand(file)}>
                ⛶ Open fullscreen
              </button>
            </div>
          )}

          {/* ODT / DOCX / DOC — show extracted text if available */}
          {['odt','docx','doc'].includes(ext) && (
            <div className="detail-section">
              <div className="detail-section-title">📝 Document Preview</div>
              {file.ocr_text && (
                <div className="detail-ocr">{file.ocr_text.slice(0, 800)}{file.ocr_text.length > 800 ? '…' : ''}</div>
              )}
              <button className="detail-expand-btn" onClick={() => onExpand && onExpand(file)}>
                ⛶ Open full preview
              </button>
            </div>
          )}

          {/* XLSX / XLS — expand to SheetJS viewer */}
          {['xlsx','ods','xls'].includes(ext) && (
            <div className="detail-section">
              <button className="detail-expand-btn" onClick={() => onExpand && onExpand(file)}>
                ⛶ Open spreadsheet preview
              </button>
            </div>
          )}

          {/* PPTX / PPT / ODP / KEY — expand button */}
          {['pptx','ppt','odp','key'].includes(ext) && (
            <div className="detail-section">
              <button className="detail-expand-btn" onClick={() => onExpand && onExpand(file)}>
                ⛶ Open full preview
              </button>
            </div>
          )}

          {/* Audio / Video / Code — expand button */}
          {(file.category === 'audio' || file.category === 'video' ||
            ['mp4','webm','mov','mp3','wav','ogg','m4a'].includes(ext) ||
            (file.category === 'code' && file.ocr_text)) && (
            <div className="detail-section">
              <button className="detail-expand-btn" onClick={() => onExpand && onExpand(file)}>
                ⛶ Open full preview
              </button>
            </div>
          )}

          {/* Generic OCR text for other documents */}
          {file.ocr_text && !['pdf','odt','docx','doc','xlsx','xls'].includes(ext) && (
            <div className="detail-section">
              <div className="detail-section-title">📝 Extracted Text</div>
              <div className="detail-ocr">{file.ocr_text}</div>
            </div>
          )}

          <div className="detail-section">
            <div className="detail-row">
              <span className="detail-label">📁 Path</span>
              <span className="detail-value">{file.auto_path}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">🕐 Uploaded</span>
              <span className="detail-value">{formatDate(file.uploaded_at)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">🆔 File ID</span>
              <span className="detail-value mono">{file.file_id}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">🔒 Private Cloud</span>
              <span className="detail-value mono">{file.bucket}</span>
            </div>
          </div>

          {getFilePacks(file).length > 0 ? (
            <div className="detail-section">
              <div className="detail-section-title">🗂️ Content Type</div>
              <div className="file-labels">
                {getFilePacks(file).map(pack => (
                  <span key={pack} className={`file-label l2-${pack}`}>
                    {L2_PACK_ICONS[pack]} {pack}
                  </span>
                ))}
              </div>
              <div className="file-labels" style={{marginTop:6}}>
                {file.content_labels.map((label, i) => (
                  <span key={i} className="file-label file-label-content">{label}</span>
                ))}
              </div>
            </div>
          ) : (
            file.category !== 'video' &&
            file.category !== 'audio' &&
            file.category !== 'archive' &&
            file.category !== 'executable' &&
            file.category !== 'security' &&
            file.processing_status === 'complete' && (
              <div className="detail-section">
                <div className="detail-section-title">🗂️ Content Type</div>
                <div className="ai-limit-badge">
                  {file.classification_status === 'skipped_quota'
                    ? (quota?.ai_labels_used_month < (quota?.ai_labels_per_month || 500)
                        ? '🏷️ This file was not AI-classified (uploaded when no AI budget was available). Re-upload to classify it now.'
                        : '🏷️ AI labelling limit reached for your plan. Upgrade to classify more files.')
                    : file.classification_status === 'stored_no_ai'
                    ? '🏷️ AI processing for this file type is coming in a future update.'
                    : file.classification_status === 'complete'
                    ? '🗂️ No content type detected for this file.'
                    : '🏷️ Not yet classified — processing may still be in progress.'}
                </div>
              </div>
            )
          )}

          {(file.category === 'image' || file.ai_labels?.length > 0) && (
            <div className="detail-section">
              <div className="detail-section-title">🏷️ AI Labels</div>
              {file.ai_labels?.length > 0 ? (
                <div className="file-labels">
                  {file.ai_labels.map((label, i) => (
                    <span key={i} className="file-label">{label}</span>
                  ))}
                </div>
              ) : (
                <div className="detail-empty">
                  {file.processing_status === 'complete' ? 'No labels detected for this file' : 'Processing…'}
                </div>
              )}
            </div>
          )}

          {file.invoice_data && (
            <div className="detail-section">
              <div className="detail-section-title">🧾 Invoice Detected</div>
              <div className="invoice-grid">
                {file.invoice_data.invoice_number && (
                  <div className="invoice-row">
                    <span className="invoice-label">Number</span>
                    <span className="invoice-value">#{file.invoice_data.invoice_number}</span>
                  </div>
                )}
                {file.invoice_data.direction && (
                  <div className="invoice-row">
                    <span className="invoice-label">Type</span>
                    <span className={`invoice-badge ${file.invoice_data.direction}`}>
                      {file.invoice_data.direction === 'incoming'
                        ? '📥 Incoming (Creditor)'
                        : '📤 Outgoing (Debitor)'}
                    </span>
                  </div>
                )}
                {file.invoice_data.total_amount && (
                  <div className="invoice-row">
                    <span className="invoice-label">Total</span>
                    <span className="invoice-value gold">
                      {file.invoice_data.currency} {file.invoice_data.total_amount}
                    </span>
                  </div>
                )}
                {file.invoice_data.due_date && (
                  <div className="invoice-row">
                    <span className="invoice-label">Due Date</span>
                    <span className="invoice-value">{file.invoice_data.due_date}</span>
                  </div>
                )}
                {file.invoice_data.vendor && (
                  <div className="invoice-row">
                    <span className="invoice-label">Vendor</span>
                    <span className="invoice-value">{file.invoice_data.vendor}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="detail-section agent-teaser">
            <div className="detail-section-title">🤖 Smart Agent</div>
            <div className="agent-teaser-text">
              Ask questions about this file in natural language.
              Semantic search, summarization, and AI insights.
            </div>
            <div className="agent-coming">Available in V1.0.1 →</div>
          </div>

        </div>

        <div className="file-detail-actions">
          <button
            className="action-btn download"
            onClick={() => onDownload(file)}
            disabled={downloading || isDeleted}
          >
            {downloading ? '⏳ Preparing...' : '⬇️ Download'}
          </button>

          <button className="action-btn archive" disabled>
            🗄️ Archive
            <span className="action-btn-hint">Paid plans</span>
          </button>

          <div className="delete-wrapper">
            <button
              className={`action-btn delete ${deleteConfirm ? 'confirm' : ''}`}
              onClick={() => onDelete(file)}
              disabled={deleting || isDeleted}
            >
              {deleting ? '⏳ Deleting...' :
               deleteConfirm ? '⚠️ Confirm Delete' :
               isDeleted ? '🗑️ Deleted' : '🗑️ Delete'}
            </button>
            <div className="delete-info">
              ℹ️ Soft delete · Restore available 30 days · <a href="mailto:support@dbos.world">Contact Support</a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DETAIL PANEL PDF VIEWER (compact, 3 pages)
// ─────────────────────────────────────────────

function DetailPdfViewer({ url }) {
  const containerRef = React.useRef(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container || !url) return;
    container.innerHTML = '';

    fetch(url).then(r => r.arrayBuffer()).then(buf => {
      pdfjsLib.getDocument({ data: buf }).promise
      .then(async (pdf) => {
        const pageCount = Math.min(pdf.numPages, 3);
        for (let i = 1; i <= pageCount; i++) {
          if (cancelled) break;
          const page   = await pdf.getPage(i);
          const scale  = Math.min((container.clientWidth || 320) / page.getViewport({ scale: 1 }).width, 1.2);
          const vp     = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width  = vp.width;
          canvas.height = vp.height;
          canvas.className = 'pdf-page-canvas';
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        }
        if (!cancelled && pdf.numPages > 3) {
          const note = document.createElement('div');
          note.className = 'pdf-page-note';
          note.textContent = `Showing 3 of ${pdf.numPages} pages`;
          container.appendChild(note);
        }
      })
      .catch(() => { if (!cancelled) setError('PDF render failed'); });
    }).catch(() => { if (!cancelled) setError('Could not load PDF'); });

    return () => { cancelled = true; };
  }, [url]);

  if (error) return <div className="detail-pdf-error">{error}</div>;
  return <div className="detail-pdf-container" ref={containerRef} />;
}

// ─────────────────────────────────────────────
// LIGHTBOX — Smart multi-format renderer
// ─────────────────────────────────────────────

function Lightbox({ file, files = [], onSelect, onClose, getSignedUrl, signedUrls }) {
  const [url, setUrl]         = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError]     = React.useState(null);

  const ext     = (file.extension || '').toLowerCase();
  const idx     = files.findIndex(f => f.file_id === file.file_id);
  const hasPrev = idx > 0;
  const hasNext = idx >= 0 && idx < files.length - 1;
  const goPrev  = () => { if (hasPrev) onSelect(files[idx - 1]); };
  const goNext  = () => { if (hasNext) onSelect(files[idx + 1]); };

  const URL_NEEDED_EXTS = new Set(['pdf','docx','doc','odt','xlsx','xls','mp4','webm','mov','mp3','wav','ogg','m4a']);
  const needsUrl = URL_NEEDED_EXTS.has(ext) || file.category === 'image';

  React.useEffect(() => {
    let active = true;
    setUrl(null); setError(null);
    if (needsUrl && getSignedUrl) {
      setLoading(true);
      getSignedUrl(file)
        .then(u  => { if (active) { setUrl(u);  setLoading(false); } })
        .catch(() => { if (active) { setError('Could not load preview'); setLoading(false); } });
    }
    return () => { active = false; };
  }, [file.file_id]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const renderContent = () => {
    if (loading) return (
      <div className="lightbox-msg">
        <div className="lb-spinner" />
        <span>Loading preview…</span>
      </div>
    );
    if (error) return <div className="lightbox-msg lightbox-error">{error}</div>;

    // ── Images ──────────────────────────────────────────────────
    if (file.category === 'image') {
      if (url) return <img className="lightbox-img" src={url} alt={file.filename} />;
      if (signedUrls?.[file.file_id]?.preview || signedUrls?.[file.file_id]?.thumbnail || file.preview_b64 || file.thumbnail_b64)
        return <img className="lightbox-img" src={signedUrls?.[file.file_id]?.preview || signedUrls?.[file.file_id]?.thumbnail || file.preview_b64 || file.thumbnail_b64} alt={file.filename} />;
      return <div className="lightbox-msg">No preview available</div>;
    }

    // ── PDF — PDF.js canvas renderer ────────────────────────────
    if (ext === 'pdf' && url) {
      return <PdfViewer url={url} />;
    }

    // ── DOCX / DOC — docx-preview ───────────────────────────────
    if (['docx','doc'].includes(ext) && url) {
      return <DocxViewer url={url} />;
    }

    // ── ODT — server-extracted text ─────────────────────────────
    if (ext === 'odt') {
      return file.ocr_text
        ? <div className="lightbox-text">{file.ocr_text}</div>
        : <div className="lightbox-msg">No text extracted yet</div>;
    }

    // ── PPTX / PPT / ODP — first slide thumbnail or fallback ────
    if (['pptx','odp','ppt'].includes(ext)) {
      return (
        <div className="lightbox-msg">
          <div className="lightbox-bigicon">📽️</div>
          <div style={{color:'#fff',marginBottom:12}}>{file.filename}</div>
          {(signedUrls?.[file.file_id]?.thumbnail || file.thumbnail_b64) && (
            <img className="lightbox-img" src={signedUrls?.[file.file_id]?.thumbnail || file.thumbnail_b64} alt="First slide preview" style={{maxHeight:'60vh',marginBottom:16}} />
          )}
          <a href={url} style={{color:'#adf',fontSize:13}} target="_blank" rel="noreferrer">⬇️ Download to view full presentation</a>
        </div>
      );
    }

    // ── XLSX / XLS — SheetJS ────────────────────────────────────
    if (['xlsx','xls'].includes(ext) && url) {
      return <SheetViewer url={url} />;
    }

    // ── Video ───────────────────────────────────────────────────
    if (['mp4','webm','mov'].includes(ext) && url) {
      return (
        <video className="lightbox-video" controls>
          <source src={url} />
        </video>
      );
    }

    // ── Audio ───────────────────────────────────────────────────
    if (['mp3','wav','ogg','m4a'].includes(ext) && url) {
      return (
        <div className="lightbox-audio-wrap">
          <div className="lightbox-bigicon">🎵</div>
          <div className="lightbox-audio-name">{file.filename}</div>
          <audio className="lightbox-audio" controls>
            <source src={url} />
          </audio>
        </div>
      );
    }

    // ── Code / Text ──────────────────────────────────────────────
    if (file.ocr_text || file.category === 'code') {
      return <CodeViewer text={file.ocr_text || ''} extension={ext} />;
    }

    // ── Fallback ─────────────────────────────────────────────────
    return (
      <div className="lightbox-msg">
        <div className="lightbox-bigicon">{CATEGORY_ICONS[file.category] || '📁'}</div>
        <div>{file.filename}</div>
        <div className="lightbox-sub">No inline preview — download to view</div>
      </div>
    );
  };

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}>✕</button>
      {hasPrev && <button className="lightbox-arrow left"  onClick={(e) => { e.stopPropagation(); goPrev(); }}>‹</button>}
      {hasNext && <button className="lightbox-arrow right" onClick={(e) => { e.stopPropagation(); goNext(); }}>›</button>}
      <div className="lightbox-stage" onClick={(e) => e.stopPropagation()}>
        {renderContent()}
      </div>
      <div className="lightbox-caption">{file.filename} · {idx + 1} of {files.length}</div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PDF VIEWER — PDF.js multi-page canvas
// ─────────────────────────────────────────────

function PdfViewer({ url }) {
  const containerRef = React.useRef(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container || !url) return;
    container.innerHTML = '';

    fetch(url).then(r => r.arrayBuffer()).then(buf => {
      pdfjsLib.getDocument({ data: buf }).promise
      .then(async (pdf) => {
        if (cancelled) return;
        for (let i = 1; i <= Math.min(pdf.numPages, 10); i++) {
          if (cancelled) break;
          const page   = await pdf.getPage(i);
          const scale  = Math.min(container.clientWidth / page.getViewport({ scale: 1 }).width, 1.5);
          const vp     = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width  = vp.width;
          canvas.height = vp.height;
          canvas.className = 'pdf-page-canvas';
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        }
        if (!cancelled && pdf.numPages > 10) {
          const note = document.createElement('div');
          note.className = 'pdf-page-note';
          note.textContent = `Showing first 10 of ${pdf.numPages} pages — download to view all`;
          container.appendChild(note);
        }
      })
      .catch(() => { if (!cancelled) setError('PDF preview failed — download to view'); });
    }).catch(() => { if (!cancelled) setError('Could not load PDF'); });

    return () => { cancelled = true; };
  }, [url]);

  if (error) return <div className="lightbox-msg lightbox-error">{error}</div>;
  return <div className="pdf-viewer-container" ref={containerRef} />;
}

// ─────────────────────────────────────────────
// DOCX VIEWER — docx-preview HTML render
// ─────────────────────────────────────────────

function DocxViewer({ url }) {
  const containerRef = React.useRef(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!url || !containerRef.current) return;
    import('docx-preview').then(({ renderAsync }) => {
      fetch(url)
        .then(r => r.blob())
        .then(blob => renderAsync(blob, containerRef.current, null, {
          className: 'docx-render',
          inWrapper: false,
          ignoreWidth: true,
        }))
        .catch(() => setError('DOCX preview failed — download to view'));
    }).catch(() => setError('DOCX renderer not available'));
  }, [url]);

  if (error) return <div className="lightbox-msg lightbox-error">{error}</div>;
  return <div className="docx-viewer-container" ref={containerRef} />;
}

// ─────────────────────────────────────────────
// SHEET VIEWER — SheetJS HTML table render
// ─────────────────────────────────────────────

function SheetViewer({ url }) {
  const [html, setHtml]   = React.useState('');
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!url) return;
    import('xlsx').then(XLSX => {
      fetch(url)
        .then(r => r.arrayBuffer())
        .then(buf => {
          const wb    = XLSX.read(buf, { type: 'array' });
          const ws    = wb.Sheets[wb.SheetNames[0]];
          const table = XLSX.utils.sheet_to_html(ws, { editable: false });
          setHtml(table);
        })
        .catch(() => setError('Spreadsheet preview failed — download to view'));
    }).catch(() => setError('Sheet renderer not available'));
  }, [url]);

  if (error) return <div className="lightbox-msg lightbox-error">{error}</div>;
  if (!html)  return <div className="lightbox-msg"><div className="lb-spinner" /><span>Rendering sheet…</span></div>;
  return (
    <div className="sheet-viewer-container">
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

// ─────────────────────────────────────────────
// CODE VIEWER — highlight.js syntax highlight
// ─────────────────────────────────────────────

function CodeViewer({ text, extension }) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (ref.current) hljs.highlightElement(ref.current);
  }, [text]);

  const langMap = {
    js:'javascript', ts:'typescript', jsx:'javascript', tsx:'typescript',
    py:'python', sh:'bash', yml:'yaml', yaml:'yaml', json:'json',
    html:'html', css:'css', sql:'sql', md:'markdown',
  };
  const lang = langMap[extension] || 'plaintext';

  return (
    <div className="code-viewer-container">
      <pre><code ref={ref} className={`language-${lang}`}>{text || '(empty file)'}</code></pre>
    </div>
  );
}
export default SmartData;
